/**
 *
 * Setup Users
 *
 */

import React, { memo, useEffect } from 'react';

import { Badge, Box, HStack, Flex, Stack, Text } from '@chakra-ui/react';
import { useFlags } from '@flagsmith/flagsmith/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import {
  AddButton,
  Card,
  FilterComponent,
  IconTypes,
  PageHeaderWithIcon,
  StackedCell,
  UserIcon,
  WithResourceIcon,
} from 'app/components';
import { CustomTable2 as Table } from 'app/components/DataDisplay/NewTable/Table';
import { errorToast } from 'app/components/Toast';
import { featureFlags, isFeatureEnabled } from 'app/flagsmith/features';
import { useApp } from 'containers/App/hooks/useApp';
import { selectUser } from 'containers/App/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { NoPermissionMessage } from 'containers/Setup/NoPermissionMessage';
import { useTrackApiCall } from 'hooks/useTrackApiCall';
import { formatDate } from 'utils/date';

import { ResetMFAButton } from './ResetMFAButton';
import { selectSetupUsers } from './selectors';
import { actions } from './slice';
import { useResetUserMFA } from './useResetUserMFA';
import { renderActions } from './utils';

export const SetupUsers = memo(() => {
  //useInjector(sliceKey, reducer, setupUsersSaga);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { renderCloudsFromAccountIds } = useCloudAccountId();

  const { users, deleteUser } = useSelector(selectSetupUsers);
  const deleteUserCall = useTrackApiCall(deleteUser);
  const user = useSelector(selectUser);
  const loggedInUserId = user?.info?.id;
  const { hasPermissionForPage } = useApp();
  const flags = useFlags([featureFlags.mfa]);
  const showMFA = isFeatureEnabled(flags[featureFlags.mfa]);
  const isMaster = user?.info?.organization === 'master';
  // Only organization administrators of tenant realms can reset authenticators.
  const mfaReset = useResetUserMFA(
    () => dispatch(actions.loadUsers()),
    showMFA && !isMaster,
  );

  useEffect(() => {
    if (hasPermissionForPage) {
      dispatch(actions.loadUsers());
      dispatch(actions.resetUserForm({}));
    }
  }, [dispatch, hasPermissionForPage]);

  useEffect(() => {
    if (deleteUserCall.success) {
      deleteUserCall.done();
    }
  }, [dispatch, deleteUserCall]);

  const onEdit = row => {
    navigate(`/setup/users/edit/${row.id}`);
  };

  const onDelete = row => {
    const { id, organization } = row;
    if (loggedInUserId !== id) {
      dispatch(
        actions.deleteUser({
          id,
          organization,
        }),
      );
    } else {
      errorToast({
        title: `Failed to delete the user`,
        description: 'Cannot delete own account',
      });
    }
  };

  const handlePaginationUpdate = pageInfo => {
    dispatch(actions.updateUserPageInfo(pageInfo));
  };

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Username</Box>,
      accessor: 'username',
      align: 'left',
      render: ({ value, row }) => {
        return (
          <WithResourceIcon resourceType={IconTypes.User} iconSize="md">
            <StackedCell upper={value} lower={row?.email} />
          </WithResourceIcon>
        );
      },
    },
    ...(showMFA
      ? [
          {
            header: '2FA',
            accessor: 'mfa_enabled',
            align: 'center',
            render: ({ value, row }) => (
              <HStack justify="center" spacing={2}>
                <Badge
                  color={
                    value === true
                      ? customTheme.colors.green[300]
                      : customTheme.colors.gray[300]
                  }
                  bg={
                    value === true
                      ? customTheme.colors.green[50]
                      : customTheme.colors.gray[50]
                  }
                  borderRadius={4}
                  px={2}
                  py={1}
                >
                  {value === true
                    ? 'Enabled'
                    : value === false
                    ? 'Not enabled'
                    : 'Unknown'}
                </Badge>
                {value === true &&
                  mfaReset.canReset &&
                  !isMaster &&
                  row.id !== loggedInUserId && (
                    <ResetMFAButton
                      user={row}
                      isResetting={mfaReset.resettingId === row.id}
                      error={mfaReset.errorFor(row.id)}
                      onReset={mfaReset.reset}
                    />
                  )}
              </HStack>
            ),
          },
        ]
      : []),
    {
      header: 'Clouds',
      accessor: 'account_ids',
      align: 'left',
      render: ({ value }) => renderCloudsFromAccountIds(value),
    },
    {
      header: 'Roles Assigned',
      accessor: 'roles',
      render: ({ value }) => value.length,
      align: 'center',
    },
    {
      header: 'Groups',
      accessor: 'user_groups',
      render: ({ value }) => value.length,
      align: 'center',
    },
    {
      header: 'Accounts Assigned',
      render: ({ row }) => {
        return (
          <StackedCell
            upper={row?.account_ids.length ?? 0}
            lower={
              <Text>
                Auto addition:{' '}
                <Text
                  display="inline-block"
                  color={row.auto_add_accounts ? 'red' : 'green'}
                >
                  {row.auto_add_accounts ? 'On' : 'Off'}
                </Text>
              </Text>
            }
          />
        );
      },
      align: 'left',
    },
    {
      header: 'Created By',
      align: 'left',
      render: ({ row }) => {
        return (
          <StackedCell
            upper={row.creator?.username ?? '-'}
            lower={formatDate(row.creation_time)}
          />
        );
      },
    },
    {
      header: 'Last Update',
      accessor: 'update_time',
      align: 'left',
      render: ({ row }) => {
        return formatDate(row.update_time);
      },
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: ({ row }) =>
        renderActions({ row, onEdit, onDelete, userCount: users.totalCount }),
    },
  ];

  if (deleteUser.loading) {
    return (
      <Box>Deleting</Box> // need to replace with spinner
    );
  }

  return (
    <Card>
      {hasPermissionForPage ? (
        <Stack h="full" spacing={5} w="full">
          <HStack w="full" justify="space-between" dir="row">
            <PageHeaderWithIcon label="Users" icon={<UserIcon />} />
            <Flex>
              <FilterComponent filterPath={'/setup/users/add'}>
                <AddButton
                  label="Add User"
                  onClick={() => navigate('/setup/users/add')}
                />
              </FilterComponent>
            </Flex>
          </HStack>
          <Box flex={1}>
            <Box w={'full'} h={'full'}>
              <Table
                data={users.payload}
                columns={columns}
                isLoading={users.loading}
                pagination={{
                  totalCount: 20,
                  pageInfo: users.pageInfo,
                  onChange: handlePaginationUpdate,
                }}
              />
            </Box>
          </Box>
        </Stack>
      ) : (
        <NoPermissionMessage />
      )}
    </Card>
  );
});
